import { useEffect, useMemo, useState } from 'react'
import { createTicket, listTickets } from '../../api/feedbackReader'
import { SettingsCard, SettingsTitle } from '../../components/settings/SettingsUI'
import type { FeedbackTicket, FeedbackTicketCategory, FeedbackTicketStatus, FeedbackTicketType } from '../../types/feedback'

type TabKey = 'bug' | 'feature' | 'support' | 'mine'

const SUPPORT_CATEGORY_OPTIONS: Array<{ value: FeedbackTicketCategory; label: string }> = [
  { value: 'BILLING', label: 'Billing' },
  { value: 'ACCOUNT', label: 'Account' },
  { value: 'SECURITY', label: 'Security' },
  { value: 'PRODUCT', label: 'Product' },
  { value: 'OTHER', label: 'Other' },
]

function fmtDate(iso?: string) {
  if (!iso) return '-'
  try {
    const d = new Date(iso)
    if (Number.isNaN(d.getTime())) return '-'
    return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' })
  } catch {
    return '-'
  }
}

function statusBadgeClass(status: FeedbackTicketStatus): string {
  if (status === 'DONE') return 'border-emerald-500/30 bg-emerald-500/10 text-emerald-200'
  if (status === 'IN_PROGRESS') return 'border-sky-500/30 bg-sky-500/10 text-sky-200'
  if (status === 'PLANNED' || status === 'TRIAGED') return 'border-amber-500/30 bg-amber-500/10 text-amber-200'
  if (status === 'WONT_FIX' || status === 'DUPLICATE') return 'border-red-500/30 bg-red-500/10 text-red-200'
  return 'border-white/15 bg-white/5 text-white/80'
}

function statusLabel(status: FeedbackTicketStatus): string {
  if (status === 'IN_PROGRESS') return 'In Progress'
  if (status === 'NEEDS_INFO') return 'Needs Info'
  if (status === 'WONT_FIX') return "Won't Fix"
  if (status === 'TRIAGED') return 'Triaged'
  if (status === 'PLANNED') return 'Planned'
  if (status === 'DUPLICATE') return 'Duplicate'
  if (status === 'DONE') return 'Done'
  return 'New'
}

function typeBadgeClass(type: FeedbackTicketType): string {
  if (type === 'BUG') return 'border-red-500/30 bg-red-500/10 text-red-200'
  if (type === 'FEATURE') return 'border-violet-500/30 bg-violet-500/10 text-violet-200'
  return 'border-sky-500/30 bg-sky-500/10 text-sky-200'
}

function typeLabel(type: FeedbackTicketType): string {
  if (type === 'BUG') return 'Bug'
  if (type === 'FEATURE') return 'Feature'
  return 'Support'
}

function categoryLabel(category: FeedbackTicketCategory): string {
  if (category === 'BILLING') return 'Billing'
  if (category === 'ACCOUNT') return 'Account'
  if (category === 'SECURITY') return 'Security'
  if (category === 'PRODUCT') return 'Product'
  return 'Other'
}

function categoryBadgeClass(category: FeedbackTicketCategory): string {
  if (category === 'SECURITY') return 'border-red-500/30 bg-red-500/10 text-red-200'
  if (category === 'BILLING') return 'border-amber-500/30 bg-amber-500/10 text-amber-200'
  if (category === 'ACCOUNT') return 'border-emerald-500/30 bg-emerald-500/10 text-emerald-200'
  if (category === 'PRODUCT') return 'border-sky-500/30 bg-sky-500/10 text-sky-200'
  return 'border-white/15 bg-white/5 text-white/80'
}

export default function SettingsFeedback() {
  const [tab, setTab] = useState<TabKey>('bug')

  const [bugTitle, setBugTitle] = useState('')
  const [bugDescription, setBugDescription] = useState('')
  const [bugSteps, setBugSteps] = useState('')
  const [bugExpected, setBugExpected] = useState('')
  const [bugActual, setBugActual] = useState('')
  const [bugSubmitting, setBugSubmitting] = useState(false)

  const [featureTitle, setFeatureTitle] = useState('')
  const [featureDescription, setFeatureDescription] = useState('')
  const [featureSubmitting, setFeatureSubmitting] = useState(false)

  const [supportTitle, setSupportTitle] = useState('')
  const [supportMessage, setSupportMessage] = useState('')
  const [supportCategory, setSupportCategory] = useState<FeedbackTicketCategory>('OTHER')
  const [supportIncludeDiagnostics, setSupportIncludeDiagnostics] = useState(false)
  const [supportSubmitting, setSupportSubmitting] = useState(false)

  const [submitError, setSubmitError] = useState<string | null>(null)
  const [submitSuccess, setSubmitSuccess] = useState<string | null>(null)

  const [rows, setRows] = useState<FeedbackTicket[]>([])
  const [listLoading, setListLoading] = useState(false)
  const [listError, setListError] = useState<string | null>(null)

  async function loadMine() {
    setListLoading(true)
    setListError(null)
    try {
      const data = await listTickets({ mine: true })
      setRows(data)
    } catch (e: any) {
      setListError(e?.response?.data?.detail || e?.message || 'Failed to load submissions.')
      setRows([])
    } finally {
      setListLoading(false)
    }
  }

  useEffect(() => {
    if (tab !== 'mine') return
    void loadMine()
  }, [tab])

  const canSubmitBug = useMemo(() => bugTitle.trim().length > 0 && bugDescription.trim().length > 0, [bugTitle, bugDescription])
  const canSubmitFeature = useMemo(
    () => featureTitle.trim().length > 0 && featureDescription.trim().length > 0,
    [featureTitle, featureDescription]
  )
  const canSubmitSupport = useMemo(
    () => supportTitle.trim().length > 0 && supportMessage.trim().length > 0,
    [supportTitle, supportMessage]
  )

  async function onSubmitBug() {
    if (!canSubmitBug || bugSubmitting) return
    setBugSubmitting(true)
    setSubmitError(null)
    setSubmitSuccess(null)
    try {
      const body = [
        bugDescription.trim(),
        bugSteps.trim() ? `\n\nSteps:\n${bugSteps.trim()}` : '',
        bugExpected.trim() ? `\n\nExpected:\n${bugExpected.trim()}` : '',
        bugActual.trim() ? `\n\nActual:\n${bugActual.trim()}` : '',
      ]
        .join('')
        .trim()
      await createTicket({
        type: 'BUG',
        category: 'PRODUCT',
        title: bugTitle.trim(),
        body,
        meta: { source: 'settings_support_v1', form: 'bug' },
      })
      setBugTitle('')
      setBugDescription('')
      setBugSteps('')
      setBugExpected('')
      setBugActual('')
      setSubmitSuccess('Bug report sent.')
    } catch (e: any) {
      setSubmitError(e?.response?.data?.detail || e?.message || 'Could not submit bug report.')
    } finally {
      setBugSubmitting(false)
    }
  }

  async function onSubmitFeature() {
    if (!canSubmitFeature || featureSubmitting) return
    setFeatureSubmitting(true)
    setSubmitError(null)
    setSubmitSuccess(null)
    try {
      await createTicket({
        type: 'FEATURE',
        category: 'PRODUCT',
        title: featureTitle.trim(),
        body: featureDescription.trim(),
        meta: { source: 'settings_support_v1', form: 'feature' },
      })
      setFeatureTitle('')
      setFeatureDescription('')
      setSubmitSuccess('Feature request sent.')
    } catch (e: any) {
      setSubmitError(e?.response?.data?.detail || e?.message || 'Could not submit feature request.')
    } finally {
      setFeatureSubmitting(false)
    }
  }

  async function onSubmitSupport() {
    if (!canSubmitSupport || supportSubmitting) return
    setSupportSubmitting(true)
    setSubmitError(null)
    setSubmitSuccess(null)
    try {
      const meta: Record<string, any> = { source: 'settings_support_v1', form: 'support' }
      if (supportIncludeDiagnostics) {
        meta.diagnostics = {
          route: window.location.pathname + window.location.search,
          userAgent: navigator.userAgent,
          appVersion: (import.meta as any)?.env?.VITE_APP_VERSION || null,
          build: (import.meta as any)?.env?.MODE || null,
        }
      }
      await createTicket({
        type: 'SUPPORT',
        category: supportCategory,
        title: supportTitle.trim(),
        body: supportMessage.trim(),
        meta,
      })
      setSupportTitle('')
      setSupportMessage('')
      setSupportCategory('OTHER')
      setSupportIncludeDiagnostics(false)
      setSubmitSuccess('Support request sent.')
    } catch (e: any) {
      setSubmitError(e?.response?.data?.detail || e?.message || 'Could not submit support request.')
    } finally {
      setSupportSubmitting(false)
    }
  }

  return (
    <div className="space-y-4 text-white">
      <SettingsTitle title="Support" subtitle="Tell us what's broken, what you want next, or ask for help." />

      <div className="flex flex-wrap gap-2">
        <button
          type="button"
          onClick={() => setTab('bug')}
          className={`rounded-lg border px-3 py-1.5 text-sm font-semibold ${
            tab === 'bug' ? 'border-white/20 bg-white/10 text-white' : 'border-white/10 bg-white/5 text-white/75 hover:bg-white/10'
          }`}
        >
          Report a Bug
        </button>
        <button
          type="button"
          onClick={() => setTab('feature')}
          className={`rounded-lg border px-3 py-1.5 text-sm font-semibold ${
            tab === 'feature' ? 'border-white/20 bg-white/10 text-white' : 'border-white/10 bg-white/5 text-white/75 hover:bg-white/10'
          }`}
        >
          Request a Feature
        </button>
        <button
          type="button"
          onClick={() => setTab('support')}
          className={`rounded-lg border px-3 py-1.5 text-sm font-semibold ${
            tab === 'support' ? 'border-white/20 bg-white/10 text-white' : 'border-white/10 bg-white/5 text-white/75 hover:bg-white/10'
          }`}
        >
          Other Issue
        </button>
        <button
          type="button"
          onClick={() => setTab('mine')}
          className={`rounded-lg border px-3 py-1.5 text-sm font-semibold ${
            tab === 'mine' ? 'border-white/20 bg-white/10 text-white' : 'border-white/10 bg-white/5 text-white/75 hover:bg-white/10'
          }`}
        >
          My Submissions
        </button>
      </div>

      {submitError ? <div className="rounded-xl border border-red-500/30 bg-red-500/10 px-4 py-3 text-sm text-red-200">{submitError}</div> : null}
      {submitSuccess ? <div className="rounded-xl border border-emerald-500/30 bg-emerald-500/10 px-4 py-3 text-sm text-emerald-200">{submitSuccess}</div> : null}

      {tab === 'bug' ? (
        <SettingsCard title="Report a Bug" subtitle="Please include enough detail so we can reproduce it quickly.">
          <div className="space-y-3">
            <div>
              <label className="text-sm text-white/80">Title</label>
              <input
                type="text"
                maxLength={140}
                value={bugTitle}
                onChange={(e) => setBugTitle(e.target.value)}
                placeholder="Short summary"
                className="mt-1 w-full rounded-lg border border-white/15 bg-black/30 px-3 py-2 text-sm outline-none focus:border-white/25"
              />
            </div>
            <div>
              <label className="text-sm text-white/80">Description</label>
              <textarea
                rows={4}
                value={bugDescription}
                onChange={(e) => setBugDescription(e.target.value)}
                placeholder="What happened?"
                className="mt-1 w-full rounded-lg border border-white/15 bg-black/30 px-3 py-2 text-sm outline-none focus:border-white/25"
              />
            </div>
            <div>
              <label className="text-sm text-white/80">Steps (optional)</label>
              <textarea
                rows={3}
                value={bugSteps}
                onChange={(e) => setBugSteps(e.target.value)}
                placeholder="1) ... 2) ... 3) ..."
                className="mt-1 w-full rounded-lg border border-white/15 bg-black/30 px-3 py-2 text-sm outline-none focus:border-white/25"
              />
            </div>
            <div className="grid gap-3 md:grid-cols-2">
              <div>
                <label className="text-sm text-white/80">Expected (optional)</label>
                <textarea
                  rows={3}
                  value={bugExpected}
                  onChange={(e) => setBugExpected(e.target.value)}
                  className="mt-1 w-full rounded-lg border border-white/15 bg-black/30 px-3 py-2 text-sm outline-none focus:border-white/25"
                />
              </div>
              <div>
                <label className="text-sm text-white/80">Actual (optional)</label>
                <textarea
                  rows={3}
                  value={bugActual}
                  onChange={(e) => setBugActual(e.target.value)}
                  className="mt-1 w-full rounded-lg border border-white/15 bg-black/30 px-3 py-2 text-sm outline-none focus:border-white/25"
                />
              </div>
            </div>
            <div>
              <button
                type="button"
                onClick={() => void onSubmitBug()}
                disabled={!canSubmitBug || bugSubmitting}
                className="rounded-lg bg-brand px-4 py-2 text-sm font-semibold text-white hover:bg-brand-600 disabled:opacity-60"
              >
                {bugSubmitting ? 'Submitting...' : 'Submit bug report'}
              </button>
            </div>
          </div>
        </SettingsCard>
      ) : null}

      {tab === 'feature' ? (
        <SettingsCard title="Request a Feature" subtitle="Share what would make Pixeloop better for you.">
          <div className="space-y-3">
            <div>
              <label className="text-sm text-white/80">Title</label>
              <input
                type="text"
                maxLength={140}
                value={featureTitle}
                onChange={(e) => setFeatureTitle(e.target.value)}
                placeholder="Short summary"
                className="mt-1 w-full rounded-lg border border-white/15 bg-black/30 px-3 py-2 text-sm outline-none focus:border-white/25"
              />
            </div>
            <div>
              <label className="text-sm text-white/80">Description</label>
              <textarea
                rows={5}
                value={featureDescription}
                onChange={(e) => setFeatureDescription(e.target.value)}
                placeholder="What should we build, and why?"
                className="mt-1 w-full rounded-lg border border-white/15 bg-black/30 px-3 py-2 text-sm outline-none focus:border-white/25"
              />
            </div>
            <div>
              <button
                type="button"
                onClick={() => void onSubmitFeature()}
                disabled={!canSubmitFeature || featureSubmitting}
                className="rounded-lg bg-brand px-4 py-2 text-sm font-semibold text-white hover:bg-brand-600 disabled:opacity-60"
              >
                {featureSubmitting ? 'Submitting...' : 'Submit feature request'}
              </button>
            </div>
          </div>
        </SettingsCard>
      ) : null}

      {tab === 'support' ? (
        <SettingsCard title="Other Issue" subtitle="Ask for help with billing, account, security, or another issue.">
          <div className="space-y-3">
            <div>
              <label className="text-sm text-white/80">Subject</label>
              <input
                type="text"
                maxLength={140}
                value={supportTitle}
                onChange={(e) => setSupportTitle(e.target.value)}
                placeholder="What do you need help with?"
                className="mt-1 w-full rounded-lg border border-white/15 bg-black/30 px-3 py-2 text-sm outline-none focus:border-white/25"
              />
            </div>
            <div>
              <label className="text-sm text-white/80">Message</label>
              <textarea
                rows={5}
                value={supportMessage}
                onChange={(e) => setSupportMessage(e.target.value)}
                placeholder="Describe the issue."
                className="mt-1 w-full rounded-lg border border-white/15 bg-black/30 px-3 py-2 text-sm outline-none focus:border-white/25"
              />
            </div>
            <div>
              <label className="text-sm text-white/80">Category</label>
              <select
                value={supportCategory}
                onChange={(e) => setSupportCategory(e.target.value as FeedbackTicketCategory)}
                className="mt-1 w-full rounded-lg border border-white/15 bg-black/30 px-3 py-2 text-sm outline-none focus:border-white/25"
              >
                {SUPPORT_CATEGORY_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </div>
            <label className="inline-flex items-center gap-2 text-sm text-white/80">
              <input
                type="checkbox"
                checked={supportIncludeDiagnostics}
                onChange={(e) => setSupportIncludeDiagnostics(e.target.checked)}
              />
              Include diagnostics
            </label>
            <div>
              <button
                type="button"
                onClick={() => void onSubmitSupport()}
                disabled={!canSubmitSupport || supportSubmitting}
                className="rounded-lg bg-brand px-4 py-2 text-sm font-semibold text-white hover:bg-brand-600 disabled:opacity-60"
              >
                {supportSubmitting ? 'Submitting...' : 'Submit support request'}
              </button>
            </div>
          </div>
        </SettingsCard>
      ) : null}

      {tab === 'mine' ? (
        <SettingsCard
          title="My Submissions"
          subtitle="Track your bug reports, feature requests, and support issues."
          right={(
            <button
              type="button"
              onClick={() => void loadMine()}
              disabled={listLoading}
              className="rounded-lg border border-white/15 bg-white/5 px-3 py-1.5 text-xs font-semibold text-white/90 hover:bg-white/10 disabled:opacity-60"
            >
              {listLoading ? 'Loading...' : 'Refresh'}
            </button>
          )}
        >
          {listError ? (
            <div className="rounded-xl border border-red-500/30 bg-red-500/10 px-4 py-3 text-sm text-red-200">{listError}</div>
          ) : null}
          {!listLoading && !listError && rows.length === 0 ? (
            <div className="text-sm text-white/70">No submissions yet.</div>
          ) : null}
          {listLoading ? (
            <div className="space-y-2">
              <div className="h-12 rounded-lg border border-white/10 bg-white/5 animate-pulse" />
              <div className="h-12 rounded-lg border border-white/10 bg-white/5 animate-pulse" />
              <div className="h-12 rounded-lg border border-white/10 bg-white/5 animate-pulse" />
            </div>
          ) : null}
          {!listLoading && rows.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-white/60">
                    <th className="py-2 pr-3 font-medium">Type</th>
                    <th className="py-2 pr-3 font-medium">Category</th>
                    <th className="py-2 pr-3 font-medium">Title</th>
                    <th className="py-2 pr-3 font-medium">Status</th>
                    <th className="py-2 pr-0 font-medium">Created</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row) => (
                    <tr key={row.id} className="border-t border-white/10">
                      <td className="py-2 pr-3">
                        <span className={`inline-flex rounded-full border px-2 py-0.5 text-[11px] font-semibold ${typeBadgeClass(row.type)}`}>
                          {typeLabel(row.type)}
                        </span>
                      </td>
                      <td className="py-2 pr-3">
                        <span className={`inline-flex rounded-full border px-2 py-0.5 text-[11px] font-semibold ${categoryBadgeClass(row.category)}`}>
                          {categoryLabel(row.category)}
                        </span>
                      </td>
                      <td className="py-2 pr-3 text-white">{row.title}</td>
                      <td className="py-2 pr-3">
                        <span className={`inline-flex rounded-full border px-2 py-0.5 text-[11px] font-semibold ${statusBadgeClass(row.status)}`}>
                          {statusLabel(row.status)}
                        </span>
                      </td>
                      <td className="py-2 pr-0 text-white/70">{fmtDate(row.created_at)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : null}
        </SettingsCard>
      ) : null}
    </div>
  )
}
